// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { FieldDefinition } from "../types/modelSchemas";
import { fileToGeoJSON } from "../utils/geojsonUtils";
import { curveAxisTitle, curvePointsFromValue, parseCurveCsv, sortCurvePoints } from "../utils/xyCurve";
import { formatUtcAsLocal, inputValueToUtcIso, nowUtcIso, utcIsoToInputValue } from "../utils/datetimeField";
import PCurveGraph from "./PerformanceCurve/PCurveGraph";
import { fetchAssetGroupsWithBoundaryAssets, fetchAssetGroupsWithAssetPoints } from "../graphql/customQueries";
import { fetchDataSources } from "../api/awsLamba";
import { listAllPages } from "../utils/graphqlPagination";

import Select from "./ReusableComponents/Select";
import DatasetField from "./HistoricalModel/DatasetField";
import ModelOutputField from "./HistoricalModel/ModelOutputField";
import RulesField from "./HistoricalModel/RulesField";
import OutputDestinationField from "./HistoricalModel/OutputDestinationField";
// Groups a page for the asset pickers; each group carries its assets, so pages stay modest
const PICKER_GROUP_PAGE_SIZE = 100;

// Helper function to format JSON
const formatJSON = (jsonString) => {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonString;
  }
};

interface SchemaFieldRendererProps {
  fieldKey: string;
  field: FieldDefinition;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  /** Every field's value, for fields that describe another (a dict with validation.rulesFor names a dataset field) */
  siblingValues?: Record<string, any>;
  /** The model's owner as the wizard has it (the dataset field warns when the dataset belongs to someone else) */
  context?: { ownerType?: "personal" | "organization"; selectedOrgKey?: string; isAdmin?: boolean; editingModelId?: string; modelKind?: string | null; mergePolicy?: string | null };
}

const SchemaFieldRenderer: React.FC<SchemaFieldRendererProps> = ({
  fieldKey,
  field,
  value,
  onChange,
  error,
  siblingValues,
  context
}) => {
  const getPrimitiveValue = (val: any) => {
    if (val && typeof val === 'object' && 'value' in val) {
      return val.value;
    }
    return val;
  };

  const createValueObject = (newVal: any) => {
    // Preserve existing metadata if value was already an object
    const existingMetadata = (value && typeof value === 'object') ? value : {};
    
    // If it's just a value wrapper, we can spread it, otherwise start fresh but we want to keep things like 'unit' if they were there.
    
    const result: any = { ...existingMetadata, value: newVal };
    
    // Add unit from field definition if present and not already in value
    if (field.unit && !result.unit) {
      result.unit = field.unit;
    }
    
    return result;
  };

  const renderField = () => {
    // If it's an output field, render as a checkbox with specific styling
    const isOutput = field.section === 'output' || field.section === 'outputs' || fieldKey.startsWith('output-');

    if (isOutput) {
      const checkedValue = getPrimitiveValue(value) === true;
      // Check for allowedUnits in validation OR at top level (for outputs)
      const allowedUnits = field.validation?.allowedUnits || field.allowedUnits;
      const hasUnits = allowedUnits && allowedUnits.length > 0;
      
      // Get current unit selection or default to first allowed unit
      const selectedUnit = (value && typeof value === 'object' && value.unit) 
        ? value.unit 
        : (hasUnits ? allowedUnits[0] : field.unit);

      return (
        <div className="flex items-start py-2">
          <div className="flex items-center h-5">
            <input
              id={`checkbox-${fieldKey}`}
              type="checkbox"
              checked={checkedValue}
              onChange={(e) => {
                const isChecked = e.target.checked;
                // If checking the box and we have units, ensure unit is included in value
                if (isChecked && hasUnits) {
                  onChange({ value: true, unit: selectedUnit });
                } else {
                  onChange(createValueObject(isChecked));
                }
              }}
              className="h-4 w-4 text-eai-primary-accent focus:ring-eai-primary-accent border-gray-300 rounded cursor-pointer"
            />
          </div>
          <div className="ml-3 text-sm flex-1">
            <div className="flex items-center justify-between">
              <label 
                htmlFor={`checkbox-${fieldKey}`}
                className="font-medium text-gray-700 cursor-pointer block"
              >
                {field.label}
              </label>
              
              {/* Show unit dropdown if checked and has allowed units */}
              {checkedValue && hasUnits && (
                <Select
                  value={selectedUnit || ''}
                  onChange={(e) => onChange({ ...(value && typeof value === 'object' ? value : {}), value: true, unit: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  size="sm"
                  containerClassName="ml-2"
                  className="pr-8 pl-2"
                >
                  {allowedUnits.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </Select>
              )}
              
              {/* Show static unit if checked and has single unit but no dropdown */}
              {checkedValue && !hasUnits && field.unit && (
                <span className="text-gray-500 ml-2">{field.unit}</span>
              )}
            </div>
            
            {field.description && (
              <p className="text-gray-500 mt-1">{field.description}</p>
            )}

            {/* A HISTORICAL template's output always goes to a dataset (docs/architecture/HISTORICAL_DATA_RUNNERS.md) */}
            {checkedValue && context?.modelKind === "HISTORICAL" && (
              <OutputDestinationField
                fieldKey={fieldKey}
                value={value && typeof value === 'object' ? value : { value: true, ...(selectedUnit ? { unit: selectedUnit } : {}) }}
                onChange={onChange}
                siblingValues={siblingValues}
                context={context}
              />
            )}
             {error && (
              <p className="text-red-600 mt-1">{error}</p>
            )}
          </div>
        </div>
      );
    }

    // Width-free variant for elements that set their own width — Tailwind resolves w-32 vs w-full by stylesheet order (w-full wins), not by position in the className string, so the two must never be combined.
    const baseFieldClasses = `px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-eai-primary-accent ${
      error ? 'border-red-500' : 'border-gray-300'
    }`;
    const baseClasses = `w-full ${baseFieldClasses}`;
    // Selects get their frame from eai-select, so only the error border still needs saying.
    const selectErrorClass = error ? 'border-eai-danger focus:border-eai-danger focus:ring-eai-danger' : '';

    switch (field.type) {
      case 'string':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={getPrimitiveValue(value) ?? ''}
              onChange={(e) => onChange(createValueObject(e.target.value))}
              placeholder={field.placeholder}
              className={`flex-1 ${baseClasses}`}
            />
            {field.unit && <span className="text-gray-500 font-medium text-sm whitespace-nowrap">{field.unit}</span>}
          </div>
        );

      case 'int':
        // If allowedUnits is defined, show number input + unit dropdown
        if (field.validation?.allowedUnits && field.validation.allowedUnits.length > 0) {
          const numericValue = getPrimitiveValue(value);
          const selectedUnit = (value && typeof value === 'object' && value.unit) ? value.unit : (field.validation.allowedUnits[0] || '');

          return (
            <div className="flex space-x-2">
              <input
                type="number"
                step="1"
                value={numericValue ?? ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  const newValue = isNaN(val) ? null : val;
                  // Explicitly constructing the object here as per previous logic, but now consistent
                  onChange({ value: newValue, unit: selectedUnit });
                }}
                onWheel={(e) => e.target.blur()}
                placeholder={field.placeholder}
                min={field.validation?.min}
                max={field.validation?.max}
                className={`flex-1 ${baseClasses}`}
              />
              <Select
                value={selectedUnit}
                onChange={(e) => onChange({ value: numericValue, unit: e.target.value })}
                containerClassName="w-32"
                className={selectErrorClass}
              >
                {field.validation.allowedUnits.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
            </div>
          );
        }

        // Standard int input without unit dropdown
        return (
          <div className="flex items-center space-x-2">
            <input
              type="number"
              step="1"
              value={getPrimitiveValue(value) ?? ''}
              onChange={(e) => { 
                const val = parseInt(e.target.value); 
                onChange(createValueObject(isNaN(val) ? null : val)); 
              }}
              onWheel={(e) => e.target.blur()}
              placeholder={field.placeholder}
              min={field.validation?.min}
              max={field.validation?.max}
              className={`flex-1 ${baseClasses}`}
            />
            {field.unit && <span className="text-gray-500 font-medium text-sm whitespace-nowrap">{field.unit}</span>}
          </div>
        );

      case 'float':
        // If allowedUnits is defined, show number input + unit dropdown
        if (field.validation?.allowedUnits && field.validation.allowedUnits.length > 0) {
          const numericValue = getPrimitiveValue(value);
          const selectedUnit = (value && typeof value === 'object' && value.unit) ? value.unit : (field.validation.allowedUnits[0] || '');

          return (
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.000001"
                value={numericValue ?? ''}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  const newValue = isNaN(val) ? null : val;
                  onChange({ value: newValue, unit: selectedUnit });
                }}
                onWheel={(e) => e.target.blur()}
                placeholder={field.placeholder}
                min={field.validation?.min}
                max={field.validation?.max}
                className={`flex-1 ${baseClasses}`}
              />
              <Select
                value={selectedUnit}
                onChange={(e) => onChange({ value: numericValue, unit: e.target.value })}
                containerClassName="w-32"
                className={selectErrorClass}
              >
                {field.validation.allowedUnits.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
            </div>
          );
        }

        // Standard float input without unit dropdown
        return (
          <div className="flex items-center space-x-2">
            <input
              type="number"
              step="0.000001"
              value={getPrimitiveValue(value) ?? ''}
              onChange={(e) => { 
                const val = parseFloat(e.target.value); 
                onChange(createValueObject(isNaN(val) ? null : val)); 
              }}
              onWheel={(e) => e.target.blur()}
              placeholder={field.placeholder}
              min={field.validation?.min}
              max={field.validation?.max}
              className={`flex-1 ${baseClasses}`}
            />
            {field.unit && <span className="text-gray-500 font-medium text-sm whitespace-nowrap">{field.unit}</span>}
          </div>
        );

      case 'boolean':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={getPrimitiveValue(value) === true}
              onChange={(e) => onChange(createValueObject(e.target.checked))}
              className="h-4 w-4 text-eai-primary-accent focus:ring-eai-primary-accent border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              {field.label}
            </span>
          </div>
        );

      case 'enum': {
        if (field.validation?.multiSelect) {
          const raw = getPrimitiveValue(value);
          // Coerce a legacy scalar (field toggled to multi-select later) into an array
          const selectedValues: (string | number)[] = Array.isArray(raw)
            ? raw
            : (raw === null || raw === undefined || raw === '' ? [] : [raw]);

          const toggleOption = (optValue: string | number) => {
            const isSelected = selectedValues.some((v) => String(v) === String(optValue));
            const next = isSelected
              ? selectedValues.filter((v) => String(v) !== String(optValue))
              : [...selectedValues, optValue];
            onChange(createValueObject(next));
          };

          return (
            <div
              className={`w-full border rounded-md p-2 max-h-48 overflow-y-auto space-y-1 ${
                error ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              {field.validation?.enumOptions?.map((option: any) => (
                <label
                  key={String(option.value)}
                  className="flex items-center px-1 py-0.5 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.some((v) => String(v) === String(option.value))}
                    onChange={() => toggleOption(option.value)}
                    className="h-4 w-4 text-eai-primary-accent border-gray-300 rounded focus:ring-eai-primary-accent"
                  />
                  <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
              {(!field.validation?.enumOptions || field.validation.enumOptions.length === 0) && (
                <p className="text-sm text-gray-500 italic">No options defined</p>
              )}
            </div>
          );
        }
        return (
          <Select
            value={getPrimitiveValue(value) ?? ''}
            onChange={(e) => {
              const selectedOption = field.validation?.enumOptions?.find(
                (opt: any) => String(opt.value) === e.target.value
              );
              // Return the actual typed value (string, int, or float)
              let val;
              if (selectedOption) {
                val = selectedOption.value;
              } else {
                val = e.target.value;
              }
              onChange(createValueObject(val));
            }}
            className={selectErrorClass}
          >
            <option value="">Select an option...</option>
            {field.validation?.enumOptions?.map((option: any) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </Select>
        );
      }

      case 'datetime': {
        const storedDateTime = getPrimitiveValue(value) ?? '';
        const localPreview = formatUtcAsLocal(storedDateTime);
        return (
          <div>
            <div className="flex items-center space-x-2">
              <input
                type="datetime-local"
                value={utcIsoToInputValue(storedDateTime)}
                onChange={(e) => onChange(createValueObject(inputValueToUtcIso(e.target.value)))}
                min={utcIsoToInputValue(field.validation?.minDateTime) || undefined}
                max={utcIsoToInputValue(field.validation?.maxDateTime) || undefined}
                className={`flex-1 ${baseClasses}`}
              />
              <span className="text-gray-500 font-medium text-sm whitespace-nowrap">UTC</span>
              <button
                type="button"
                onClick={() => onChange(createValueObject(nowUtcIso()))}
                className="px-2 py-2 text-sm text-eai-primary-accent hover:text-gray-700 font-medium whitespace-nowrap"
                title="Set to the current time (UTC)"
              >
                Now
              </button>
            </div>
            {localPreview && (
              <p className="mt-1 text-xs text-gray-500">Local: {localPreview}</p>
            )}
          </div>
        );
      }

      case 'dataset':
        // HISTORICAL models (docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md): a dataset and its columns
        return <DatasetField fieldKey={fieldKey} field={field} value={value} onChange={onChange} error={error} context={context} />;

      case 'model_output':
        // Another model's output param and its columns (docs/architecture/MODEL_OUTPUT_INPUTS.md)
        return <ModelOutputField fieldKey={fieldKey} field={field} value={value} onChange={onChange} error={error} context={context} />;

      case 'dict':
        // A dict that holds the quality rules of a dataset field (validation.rulesFor) gets the per-column form
        // (stage 4, decision 5); any other dict is edited as JSON
        if ((field.validation as any)?.rulesFor) {
          const target = siblingValues?.[(field.validation as any).rulesFor];
          const columns = Array.isArray(target?.columns) ? (target.columns as string[]) : [];
          return <RulesField fieldKey={fieldKey} field={field} value={value} onChange={onChange} error={error} columns={columns} />;
        }
        // DictField handles its own value structure, but we might need to wrap it if requested. Assuming DictField returns a complex object which IS the value. For consistency, we might want { value: { ...dictContent } }? But DictField is complex. Let's see if we can apply the pattern or if it breaks things. For now, I'll assume complex types (dict, forecast, dataSource, file, boundary) manage their own objects. If the user insists on EVERYTHING being { value: ... }, I would need to wrap these too. Given the example showed primitives (float, int, string), I will apply this mainly to primitives.
        return <DictField value={value} onChange={onChange} field={field} error={error} />;

      case 'forecast':
        return <ForecastField value={value} onChange={onChange} field={field} error={error} />;

      case 'dataSource':
        return <DataSourceField value={value} onChange={onChange} field={field} error={error} />;

      case 'file_reference':
        return (
          <div className="space-y-2">
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  // For file references, the object structure is specific. We'll keep it as is for now unless specifically requested to wrap it.
                  onChange({
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    uploadPath: `pending-upload/${file.name}`, // Mock path
                    file: file // Store the actual File object
                  });
                }
              }}
              accept={field.validation?.fileTypes?.map(type => `.${type}`).join(',')}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-eai-primary-accent file:text-white hover:file:bg-gray-400"
            />
            {value && (
              <div className="text-sm text-gray-600">
                Selected: {value.fileName || (value.value && value.value.fileName) || "File selected"}
              </div>
            )}
            {field.validation?.fileTypes && (
              <div className="text-xs text-gray-500">
                Accepted formats: {field.validation.fileTypes.join(', ')}
              </div>
            )}
          </div>
        );

      case '2d_array':
        return (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              {field.description}
            </div>
            {field.validation?.columns && (
              <div className="text-xs text-gray-500">
                Expected columns: {field.validation.columns.join(', ')}
              </div>
            )}
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onChange({
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    uploadPath: `pending-upload/${file.name}`,
                    dataType: '2d_array',
                    expectedColumns: field.validation?.columns,
                    file: file // Store the actual File object
                  });
                }
              }}
              accept=".csv,.json,.xlsx"
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-eai-primary-accent file:text-white hover:file:bg-gray-400"
            />
            {value && (
              <div className="text-sm text-gray-600">
                Selected: {value.fileName || (value.value && value.value.fileName) || "File selected"}
              </div>
            )}
            <div className="text-xs text-gray-500">
              Upload CSV, JSON, or Excel file with the required columns
            </div>
          </div>
        );

      case 'asset_boundary':
        return <AssetBoundaryField field={field} value={value} onChange={onChange} />;

      case 'asset_location':
        return <AssetLocationField field={field} value={value} onChange={onChange} />;

      case 'xy_curve':
        return <XYCurveField field={field} value={value} onChange={onChange} error={error} />;

      default:
        return (
          <input
            type="text"
            value={getPrimitiveValue(value) ?? ''}
            onChange={(e) => onChange(createValueObject(e.target.value))}
            placeholder={field.placeholder}
            className={baseClasses}
          />
        );
    }
  };

  const isOutput = field.section === 'output' || field.section === 'outputs' || fieldKey.startsWith('output-');

  return (
    <div className={isOutput ? "mb-1" : "space-y-2"}>
      {/* Hide standard label for boolean and output fields since they have their own inline labels */}
      {field.type !== 'boolean' && !isOutput && (
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {renderField()}
      
      {/* Error and description are handled inside renderField for outputs to match specific layout */}
      {!isOutput && error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
      
      {!isOutput && field.description && !error && (
        <p className="text-xs text-gray-500">{field.description}</p>
      )}
    </div>
  );
};

// DataSourceField Component - Handles hierarchical data source selection
const DataSourceField = ({ value, onChange, field, error }) => {
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedTimeType, setSelectedTimeType] = useState('');
  const [selectedDataType, setSelectedDataType] = useState(''); // This might be used as Scraper Parameter ID if needed
  
  // Dynamic fetch state
  const [scraperData, setScraperData] = useState<any[]>([]);
  const [loadingScrapers, setLoadingScrapers] = useState(false);
  const [selectedScraperSource, setSelectedScraperSource] = useState('');
  const [selectedScraperParameter, setSelectedScraperParameter] = useState('');

  // Initialize from existing value
  useEffect(() => {
    if (value) {
      if (value.source) {
        setSelectedSource(value.source);
        if (value.source === 'erodeAI') {
           // Load existing configuration for Erode AI source Prioritize new property names: dataSourceName, dataSourceParameterId, dataSourceParameterName
           if (value.dataSourceName) setSelectedScraperSource(value.dataSourceName);
           else if (value.scraperSource) setSelectedScraperSource(value.scraperSource); // Fallback to old property

           // For parameter, prefer dataSourceParameterId
           if (value.dataSourceParameterId) {
             setSelectedScraperParameter(value.dataSourceParameterId);
           } else if (value.parameter) {
             // Fallback: try to parse ID from legacy formatted string "ID - Name - Unit" or "ID - Name" format. If value is a number or numeric string, it might be the ID directly.
             const valStr = String(value.parameter);
             const match = valStr.match(/^(\d+)(\s-|$)/);
             if (match) {
               setSelectedScraperParameter(parseInt(match[1]));
             } else {
               // If value is just an ID (number/string), use it directly
               setSelectedScraperParameter(value.parameter);
             }
           }
        }
      }
      if (value.timeType) setSelectedTimeType(value.timeType);
      if (value.dataType) setSelectedDataType(value.dataType);
    }
  }, []);

  // Fetch scraper data when 'erodeAI' is selected
  useEffect(() => {
    if (selectedSource === 'erodeAI' && scraperData.length === 0) {
      const loadScrapers = async () => {
        setLoadingScrapers(true);
        try {
          const data = await fetchDataSources();
          if (Array.isArray(data)) {
            setScraperData(data);
          }
        } catch (err) {
          console.error("Error fetching scrapers:", err);
        } finally {
          setLoadingScrapers(false);
        }
      };
      loadScrapers();
    }
  }, [selectedSource]);

  // Update parent when values change
  useEffect(() => {
    if (selectedSource) {
      // Base object
      const updateObj: any = {
        source: selectedSource,
        timeType: selectedTimeType,
      };

      if (selectedSource === 'erodeAI') {
        // Validation: require timeType, scraperSource, and parameter
        if (selectedTimeType && selectedScraperSource && selectedScraperParameter) {
           // Find the full parameter object from scraperData Since selectedScraperParameter is now the ID, we search by parameter_id We also match datasource_name to be safe, though ID should be unique
           const scraperObj = scraperData.find(s => 
             s.parameter_id == selectedScraperParameter && 
             s.datasource_name === selectedScraperSource
           );

           if (scraperObj) {
             // Use scraperObj.datasource_id if available, otherwise it might be undefined The user reported "dataSourceId": "AESO" which suggests datasource_id was missing or fell back. We should check if 'datasource_id' exists in the object. If not, we might not have it available in the current API response structure. But assuming it might be there:
             updateObj.dataSourceId = scraperObj.datasource_id || scraperObj.datasourceId; 
             updateObj.dataSourceName = scraperObj.datasource_name;
             updateObj.dataSourceParameterId = scraperObj.parameter_id;
             updateObj.dataSourceParameterName = scraperObj.parameter_name;
             updateObj.dataSourceParameterAlias = scraperObj.scrapealias || scraperObj.alias;
             
             // Legacy/Display fields (reconstruct composite string)
             const compositeString = `${scraperObj.parameter_id} - ${scraperObj.parameter_name}${scraperObj.unit ? ` - ${scraperObj.unit}` : ''}`;
             updateObj.parameter = compositeString;
             updateObj.dataType = compositeString;
             
             onChange(updateObj);
           } else {
             // Should not happen if UI is consistent, but safe fallback or no update
             onChange(null);
           }
        } else {
           onChange(null);
        }
      } else {
        // Standard behavior for other sources
        if (selectedTimeType && selectedDataType) {
          updateObj.dataType = selectedDataType;
          onChange(updateObj);
        } else {
          onChange(null);
        }
      }
    } else {
      onChange(null);
    }
  }, [selectedSource, selectedTimeType, selectedDataType, selectedScraperSource, selectedScraperParameter, scraperData]); // Added scraperData dependency to ensure update when data loads

  const dataSourceConfig = field.validation?.dataSourceConfig || {};

  // Get available sources
  const availableSources = Object.keys(dataSourceConfig).map(key => ({
    value: key,
    label: dataSourceConfig[key].label
  }));

  // Get available time types for selected source
  const availableTimeTypes = selectedSource && dataSourceConfig[selectedSource]
    ? Object.keys(dataSourceConfig[selectedSource].timeTypes).map(key => ({
        value: key,
        label: dataSourceConfig[selectedSource].timeTypes[key].label
      }))
    : [];

  // Get available data types for selected source and time type (Standard/Configured)
  const availableDataTypes = selectedSource && selectedTimeType && dataSourceConfig[selectedSource]?.timeTypes?.[selectedTimeType]
    ? dataSourceConfig[selectedSource].timeTypes[selectedTimeType].dataTypes
    : [];

  // Derived Scraper Options Group by datasource_name
  const scraperSources = React.useMemo(() => {
    const uniqueSources = new Map();
    scraperData.forEach(s => {
      // Use datasource_name as key to deduplicate by name
      if (s.datasource_name && !uniqueSources.has(s.datasource_name)) {
        uniqueSources.set(s.datasource_name, {
          name: s.datasource_name,
          // Capture the ID from the first occurrence
          id: s.datasource_id || s.datasourceId
        });
      }
    });
    
    // Convert to array and sort by name
    return Array.from(uniqueSources.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [scraperData]);

  // Parameters for selected scraper source
  const scraperParameters = React.useMemo(() => {
    if (!selectedScraperSource) return [];
    return scraperData
      .filter(s => s.datasource_name === selectedScraperSource)
      .map(s => ({
        id: s.parameter_id,
        label: `${s.parameter_id} - ${s.parameter_name}${s.unit ? ` - ${s.unit}` : ''}`,
        value: s.parameter_id
      }))
      .sort((a, b) => a.id - b.id);
  }, [scraperData, selectedScraperSource]);


  // Reset dependent selections when parent changes
  const handleSourceChange = (newSource) => {
    setSelectedSource(newSource);
    setSelectedTimeType('');
    setSelectedDataType('');
    setSelectedScraperSource('');
    setSelectedScraperParameter('');
  };

  const handleTimeTypeChange = (newTimeType) => {
    setSelectedTimeType(newTimeType);
    setSelectedDataType('');
    // Keep scraper selection reset or persistent? Resetting is safer for now. Actually scraper source doesn't depend on time type in the API data structure (it just exists), but the UI flow suggests we select Time Type first.
  };

  return (
    <div className="space-y-3">
      {/* Source Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Data Source *
        </label>
        <Select
          value={selectedSource}
          onChange={(e) => handleSourceChange(e.target.value)}
          containerClassName="w-full"
        >
          <option value="">Select data source...</option>
          {availableSources.map((source) => (
            <option key={source.value} value={source.value}>
              {source.label}
            </option>
          ))}
        </Select>
      </div>

      {/* Time Type Selection - only show when source is selected */}
      {selectedSource && availableTimeTypes.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time Type *
          </label>
          <Select
            value={selectedTimeType}
            onChange={(e) => handleTimeTypeChange(e.target.value)}
            containerClassName="w-full"
          >
            <option value="">Select time type...</option>
            {availableTimeTypes.map((timeType) => (
              <option key={timeType.value} value={timeType.value}>
                {timeType.label}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* DYNAMIC SCAPER SELECTION (for Erode AI) */}
      {selectedSource === 'erodeAI' && selectedTimeType && (
        <>
          {/* Scraper Source Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Source *
            </label>
            {loadingScrapers ? (
              <div className="text-sm text-gray-500">Loading sources...</div>
            ) : (
              <Select
                value={selectedScraperSource}
                onChange={(e) => {
                  setSelectedScraperSource(e.target.value);
                  setSelectedScraperParameter(''); // Reset parameter
                }}
                containerClassName="w-full"
              >
                <option value="">Select scraper source...</option>
                {scraperSources.map((src) => (
                  <option key={src.name} value={src.name}>
                    {src.id ? `${src.id} - ${src.name}` : src.name}
                  </option>
                ))}
              </Select>
            )}
          </div>

          {/* Parameter Selection */}
          {selectedScraperSource && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Parameter *
              </label>
              <Select
                value={selectedScraperParameter}
                onChange={(e) => setSelectedScraperParameter(e.target.value)}
                containerClassName="w-full"
              >
                <option value="">Select parameter...</option>
                {scraperParameters.map((param) => (
                  <option key={param.id} value={param.value}>
                    {param.label}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </>
      )}

      {/* STANDARD DATA TYPE SELECTION (for non-Erode AI sources) */}
      {selectedSource !== 'erodeAI' && selectedSource && selectedTimeType && availableDataTypes.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data Type *
          </label>
          <Select
            value={selectedDataType}
            onChange={(e) => setSelectedDataType(e.target.value)}
            containerClassName="w-full"
          >
            <option value="">Select data type...</option>
            {availableDataTypes.map((dataType) => (
              <option key={dataType} value={dataType}>
                {dataType}
              </option>
            ))}
          </Select>
        </div>
      )}

      {/* Success indicator */}
      {(
        (selectedSource === 'erodeAI' && selectedScraperSource && selectedScraperParameter) ||
        (selectedSource !== 'erodeAI' && selectedSource && selectedTimeType && selectedDataType)
      ) && (
        <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
          ✓ Data source configured: {dataSourceConfig[selectedSource]?.label} → {dataSourceConfig[selectedSource]?.timeTypes?.[selectedTimeType]?.label} → {selectedSource === 'erodeAI' ? `${selectedScraperSource} - ${selectedScraperParameter}` : selectedDataType}
        </div>
      )}
    </div>
  );
};

// ForecastField Component - Handles forecast data selection with parameters, providers, channels, and location
const ForecastField = ({ value, onChange, field, error }) => {
  const [selectedParameter, setSelectedParameter] = useState('');
  const [providerChannels, setProviderChannels] = useState<Record<string, Record<string, boolean | number[]>>>({});
  const [ensembleInputs, setEnsembleInputs] = useState<Record<string, string>>({});
  const [locationType, setLocationType] = useState<'manual' | 'assetPoint' | 'assetGeojson'>('manual');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [assetGroups, setAssetGroups] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  // Get forecast sources from field validation
  const forecastSources = field.validation?.forecastSources || {};

  // Extract unique parameters across all providers
  const uniqueParameters = React.useMemo(() => {
    const paramMap = new Map<string, { keyId: string; label: string }>();
    Object.values(forecastSources).forEach((source: any) => {
      if (source.enabled && source.parameters) {
        source.parameters.forEach((param: any) => {
          if (!paramMap.has(param.keyId)) {
            paramMap.set(param.keyId, { keyId: param.keyId, label: param.label });
          }
        });
      }
    });
    return Array.from(paramMap.values());
  }, [forecastSources]);

  // Get providers that have the selected parameter
  const availableProviders = React.useMemo(() => {
    if (!selectedParameter) return [];
    const providers: { sourceKey: string; label: string; param: any }[] = [];
    Object.entries(forecastSources).forEach(([sourceKey, source]: [string, any]) => {
      if (source.enabled && source.parameters) {
        const param = source.parameters.find((p: any) => p.keyId === selectedParameter);
        if (param) {
          providers.push({
            sourceKey,
            label: sourceKey, // Could be enhanced with a label lookup
            param
          });
        }
      }
    });
    return providers;
  }, [forecastSources, selectedParameter]);

  // Initialize from existing value
  useEffect(() => {
    if (value) {
      if (value.parameter) setSelectedParameter(value.parameter);
      if (value.providers) {
        const channels: Record<string, Record<string, boolean | number[]>> = {};
        const ensembleInputsInit: Record<string, string> = {};
        Object.entries(value.providers).forEach(([providerKey, providerData]: [string, any]) => {
          if (providerData.channels) {
            channels[providerKey] = { ...providerData.channels };
            if (Array.isArray(providerData.channels.ensemble)) {
              ensembleInputsInit[providerKey] = providerData.channels.ensemble.join(', ');
            }
          }
        });
        setProviderChannels(channels);
        setEnsembleInputs(ensembleInputsInit);
      }
      if (value.location) {
        if (value.location.type === 'manual') {
          setLocationType('manual');
          setLatitude(value.location.latitude?.toString() || '');
          setLongitude(value.location.longitude?.toString() || '');
        } else if (value.location.type === 'assetPoint' || value.location.type === 'assetGeojson') {
          setLocationType(value.location.type);
          setSelectedAssetIds(value.location.assetIds || []);
        }
      }
    }
  }, []);

  // Fetch assets when asset selection is chosen
  useEffect(() => {
    const fetchAssets = async () => {
      if ((locationType === 'assetPoint' || locationType === 'assetGeojson') && assetGroups.length === 0) {
        try {
          setLoadingAssets(true);
          // Every page of groups (AppSync returns one page per call)
          const { items: groupItems } = await listAllPages<any>(
            fetchAssetGroupsWithBoundaryAssets,
            "listAssetGroups",
            { limit: PICKER_GROUP_PAGE_SIZE },
            { label: "fetchAssetGroupsWithBoundaryAssets" }
          );

          const groupsWithAssets = groupItems
            .map(group => ({
              id: group.id,
              name: group.name,
              description: group.description,
              assets: group.assets.items.map(asset => ({
                ...asset,
                hasPoint: !!(asset.lat && asset.lng),
                hasGeojson: !!asset.geojson
              }))
            }))
            .filter(group => group.assets.length > 0);

          setAssetGroups(groupsWithAssets);
        } catch (error) {
          console.error('Error fetching assets:', error);
        } finally {
          setLoadingAssets(false);
        }
      }
    };

    fetchAssets();
  }, [locationType]);

  // Build and update parent value when selections change
  useEffect(() => {
    // Check if we have at least one provider with at least one channel selected
    const hasProviderSelection = Object.entries(providerChannels).some(([_, channels]) =>
      Object.values(channels).some(v => v === true || (Array.isArray(v) && v.length > 0))
    );

    if (!selectedParameter || !hasProviderSelection) {
      onChange(null);
      return;
    }

    // Build location data
    let locationData = null;
    if (locationType === 'manual' && latitude && longitude) {
      locationData = {
        type: 'manual',
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude)
      };
    } else if ((locationType === 'assetPoint' || locationType === 'assetGeojson') && selectedAssetIds.length > 0) {
      locationData = {
        type: locationType,
        assetIds: selectedAssetIds
      };
    }

    if (!locationData) {
      onChange(null);
      return;
    }

    // Build providers object with only selected channels
    const providers: Record<string, any> = {};
    availableProviders.forEach(({ sourceKey, param }) => {
      const channels = providerChannels[sourceKey];
      if (channels) {
        const activeChannels: Record<string, boolean | number[]> = {};
        Object.entries(channels).forEach(([channelKey, channelValue]) => {
          if (channelValue === true || (Array.isArray(channelValue) && channelValue.length > 0)) {
            activeChannels[channelKey] = channelValue;
          }
        });
        if (Object.keys(activeChannels).length > 0) {
          providers[sourceKey] = {
            extractionParam: param.extractionParam,
            channels: activeChannels
          };
        }
      }
    });

    if (Object.keys(providers).length === 0) {
      onChange(null);
      return;
    }

    onChange({
      parameter: selectedParameter,
      providers,
      location: locationData
    });
  }, [selectedParameter, providerChannels, locationType, latitude, longitude, selectedAssetIds, availableProviders]);

  // Handle parameter change - reset provider selections
  const handleParameterChange = (newParameter: string) => {
    setSelectedParameter(newParameter);
    setProviderChannels({});
    setEnsembleInputs({});
  };

  // Handle channel checkbox toggle
  const handleChannelToggle = (providerKey: string, channelKey: string, checked: boolean) => {
    setProviderChannels(prev => {
      const updated = { ...prev };
      if (!updated[providerKey]) {
        updated[providerKey] = {};
      }
      if (channelKey === 'ensemble') {
        // For ensemble, set to empty array when checked, remove when unchecked
        if (checked) {
          updated[providerKey] = { ...updated[providerKey], ensemble: [] };
        } else {
          const { ensemble, ...rest } = updated[providerKey];
          updated[providerKey] = rest;
          // Clear the ensemble input
          setEnsembleInputs(prev => {
            const { [providerKey]: _, ...rest } = prev;
            return rest;
          });
        }
      } else {
        updated[providerKey] = { ...updated[providerKey], [channelKey]: checked };
      }
      return updated;
    });
  };

  // Handle ensemble quantiles input
  const handleEnsembleInput = (providerKey: string, inputValue: string) => {
    setEnsembleInputs(prev => ({ ...prev, [providerKey]: inputValue }));

    // Parse comma-separated values to numbers
    const quantiles = inputValue
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n) && n > 0 && n < 100);

    setProviderChannels(prev => {
      const updated = { ...prev };
      if (!updated[providerKey]) {
        updated[providerKey] = {};
      }
      updated[providerKey] = { ...updated[providerKey], ensemble: quantiles };
      return updated;
    });
  };

  // Handle asset selection
  const handleAssetToggle = (assetId: string) => {
    setSelectedAssetIds(prev =>
      prev.includes(assetId)
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const allowLocationInput = field.validation?.allowLocationInput !== false;
  const allowAssetSelection = field.validation?.allowAssetLocationSelection !== false;

  // Check if an asset is valid for the current location type
  const isAssetValidForLocationType = (asset: any) => {
    if (locationType === 'assetPoint') return asset.hasPoint;
    if (locationType === 'assetGeojson') return asset.hasGeojson;
    return true;
  };

  return (
    <div className="space-y-4">
      {/* Parameter Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Forecast Parameter *
        </label>
        {uniqueParameters.length === 0 ? (
          <div className="text-sm text-gray-500 italic p-3 bg-gray-50 rounded border border-gray-200">
            No forecast parameters configured for this field.
          </div>
        ) : (
          <Select
            value={selectedParameter}
            onChange={(e) => handleParameterChange(e.target.value)}
            containerClassName="w-full"
          >
            <option value="">Select parameter...</option>
            {uniqueParameters.map((param) => (
              <option key={param.keyId} value={param.keyId}>
                {param.label}
              </option>
            ))}
          </Select>
        )}
      </div>

      {/* Provider and Channel Selection */}
      {selectedParameter && availableProviders.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Providers & Channels *
          </label>
          <div className="space-y-3">
            {availableProviders.map(({ sourceKey, label, param }) => {
              const channels = param.channels || [];
              const selectedChannels = providerChannels[sourceKey] || {};
              const hasAnyChannelSelected = Object.values(selectedChannels).some(
                v => v === true || (Array.isArray(v) && v.length > 0)
              );

              return (
                <div
                  key={sourceKey}
                  className={`border rounded-md ${hasAnyChannelSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className="px-3 py-2 font-medium text-sm text-gray-700 border-b border-gray-200">
                    {label}
                  </div>
                  <div className="p-3 space-y-2">
                    {channels.map((channel: string) => {
                      const isEnsemble = channel === 'ensemble';
                      const isChecked = isEnsemble
                        ? Array.isArray(selectedChannels.ensemble)
                        : selectedChannels[channel] === true;

                      return (
                        <div key={channel} className="space-y-1">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => handleChannelToggle(sourceKey, channel, e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700 capitalize">{channel}</span>
                          </label>
                          {/* Ensemble quantiles input */}
                          {isEnsemble && isChecked && (
                            <div className="ml-6">
                              <input
                                type="text"
                                value={ensembleInputs[sourceKey] || ''}
                                onChange={(e) => handleEnsembleInput(sourceKey, e.target.value)}
                                placeholder="Enter quantiles (e.g., 10, 50, 90)"
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Comma-separated values between 1-99
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Location Selection */}
      {selectedParameter && availableProviders.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Location *
          </label>

          {/* Tabs for location input type */}
          <div className="flex border-b border-gray-200">
            {allowLocationInput && (
              <button
                type="button"
                onClick={() => setLocationType('manual')}
                className={`px-4 py-2 text-sm font-medium ${
                  locationType === 'manual'
                    ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Manual Coordinates
              </button>
            )}
            {allowAssetSelection && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setLocationType('assetPoint');
                    setSelectedAssetIds([]);
                  }}
                  className={`px-4 py-2 text-sm font-medium ${
                    locationType === 'assetPoint'
                      ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Asset Point
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLocationType('assetGeojson');
                    setSelectedAssetIds([]);
                  }}
                  className={`px-4 py-2 text-sm font-medium ${
                    locationType === 'assetGeojson'
                      ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Asset Shape
                </button>
              </>
            )}
          </div>

          {/* Manual Coordinates Tab */}
          {locationType === 'manual' && allowLocationInput && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Latitude
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  placeholder="e.g., 40.7128"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-eai-primary-accent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Longitude
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  placeholder="e.g., -74.0060"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-eai-primary-accent"
                />
              </div>
            </div>
          )}

          {/* Asset Selection (Point or Shape) */}
          {(locationType === 'assetPoint' || locationType === 'assetGeojson') && allowAssetSelection && (
            <div className="pt-2">
              <p className="text-xs text-gray-500 mb-2">
                {locationType === 'assetPoint'
                  ? 'Select assets to use their lat/lng coordinates'
                  : 'Select assets to use their GeoJSON boundaries'}
              </p>
              {loadingAssets ? (
                <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded border border-gray-200">
                  Loading assets...
                </div>
              ) : assetGroups.length === 0 ? (
                <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded border border-gray-200">
                  No assets found. Create assets first.
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md">
                  {assetGroups.map((group: any) => {
                    const validAssets = group.assets.filter(isAssetValidForLocationType);
                    if (validAssets.length === 0) return null;

                    return (
                      <div key={group.id} className="border-b last:border-b-0">
                        <div className="bg-gray-100 px-3 py-2 border-b sticky top-0">
                          <div className="text-sm font-semibold text-gray-700">
                            {group.name}
                          </div>
                        </div>
                        {validAssets.map((asset: any) => (
                          <label
                            key={asset.id}
                            className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
                              selectedAssetIds.includes(asset.id) ? 'bg-blue-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAssetIds.includes(asset.id)}
                              onChange={() => handleAssetToggle(asset.id)}
                              className="h-4 w-4 text-eai-primary-accent focus:ring-eai-primary-accent border-gray-300 rounded"
                            />
                            <div className="ml-3 flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                {asset.name}
                              </div>
                              <div className="text-xs text-gray-400">
                                {asset.hasPoint && `Point: ${asset.lat?.toFixed(4)}, ${asset.lng?.toFixed(4)}`}
                                {asset.hasPoint && asset.hasGeojson && ' | '}
                                {asset.hasGeojson && 'Has boundary'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
              {selectedAssetIds.length > 0 && (
                <div className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                  {selectedAssetIds.length} asset{selectedAssetIds.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// DictField Component - Handles JSON dictionary input with validation
const DictField = ({ value, onChange, field, error }) => {
  const [jsonString, setJsonString] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [isValid, setIsValid] = useState(true);

  // Initialize jsonString from value
  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        setJsonString(value);
      } else {
        setJsonString(JSON.stringify(value, null, 2));
      }
    } else {
      setJsonString('');
    }
  }, []);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setJsonString(newValue);

    // Try to parse JSON
    if (newValue.trim() === '') {
      setJsonError(null);
      setIsValid(true);
      onChange(null);
      return;
    }

    try {
      const parsed = JSON.parse(newValue);

      // Validate required keys if specified
      if (field.validation?.requiredKeys && field.validation.requiredKeys.length > 0) {
        const missingKeys = field.validation.requiredKeys.filter(key => !(key in parsed));
        if (missingKeys.length > 0) {
          setJsonError(`Missing required keys: ${missingKeys.join(', ')}`);
          setIsValid(false);
          onChange(null);
          return;
        }
      }

      setJsonError(null);
      setIsValid(true);
      onChange(parsed);
    } catch (err) {
      setJsonError(`Invalid JSON: ${err.message}`);
      setIsValid(false);
      onChange(null);
    }
  };

  const handleFormat = () => {
    if (isValid && jsonString.trim()) {
      const formatted = formatJSON(jsonString);
      setJsonString(formatted);
    }
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <textarea
          value={jsonString}
          onChange={handleChange}
          placeholder={field.placeholder || '{\n  "key": "value"\n}'}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-eai-primary-accent font-mono text-sm ${
            jsonError ? 'border-red-500' : 'border-gray-300'
          }`}
          rows={8}
        />
        {isValid && jsonString.trim() && (
          <button
            type="button"
            onClick={handleFormat}
            className="absolute top-2 right-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
          >
            Format JSON
          </button>
        )}
      </div>

      {jsonError && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          {jsonError}
        </div>
      )}

      {isValid && jsonString.trim() && (
        <div className="text-sm text-green-600">
          ✓ Valid JSON
        </div>
      )}

      {field.validation?.requiredKeys && field.validation.requiredKeys.length > 0 && (
        <div className="text-xs text-gray-500">
          Required keys: {field.validation.requiredKeys.join(', ')}
        </div>
      )}
    </div>
  );
};

// AssetBoundaryField Component - Handles selecting existing assets or uploading boundary files TODO: REFACTOR - Move asset data fetching to Redux Current: This component directly calls GraphQL query (fetchAssetGroupsWithBoundaryAssets) Future: Update assetsSlice.ts fetchGroupsAndNestedAssets to store group names in state Then use: const assetGroups = useSelector(state => state.assets.groupsWithNames) This will eliminate direct GraphQL calls in components and centralize data management
const AssetBoundaryField = ({ field, value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    field.validation?.allowAssetReference ? 'asset' : 'file'
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [assetGroups, setAssetGroups] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);

  // Fetch asset groups with boundary assets on mount
  useEffect(() => {
    const fetchAssetGroups = async () => {
      try {
        setLoadingAssets(true);
        // Every page of groups (AppSync returns one page per call)
        const { items: groupItems } = await listAllPages<any>(
          fetchAssetGroupsWithBoundaryAssets,
          "listAssetGroups",
          { limit: PICKER_GROUP_PAGE_SIZE },
          { label: "fetchAssetGroupsWithBoundaryAssets" }
        );

        // Filter groups to only those with assets that have geojson
        const groupsWithBoundaries = groupItems
          .map(group => ({
            id: group.id,
            name: group.name,
            description: group.description,
            assets: group.assets.items.filter(asset => asset.geojson)
          }))
          .filter(group => group.assets.length > 0); // Only include groups that have assets with boundaries

        setAssetGroups(groupsWithBoundaries);
      } catch (error) {
        console.error('Error fetching asset groups:', error);
      } finally {
        setLoadingAssets(false);
      }
    };

    if (activeTab === 'asset') {
      fetchAssetGroups();
    }
  }, [activeTab]);

  // Calculate total available assets across all groups
  const totalAvailableAssets = assetGroups.reduce((sum, group) => sum + group.assets.length, 0);

  const handleAssetSelection = (assetId) => {
    const multipleAssets = field.validation?.multipleAssets !== false; // Default to true if not specified

    if (multipleAssets) {
      // Multiple selection mode
      const currentIds = value?.selectedAssetIds || [];
      const newIds = currentIds.includes(assetId)
        ? currentIds.filter(id => id !== assetId)
        : [...currentIds, assetId];

      onChange({
        mode: 'asset_reference',
        selectedAssetIds: newIds
      });
    } else {
      // Single selection mode
      onChange({
        mode: 'asset_reference',
        selectedAssetIds: [assetId]
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Use the utility function to convert file to GeoJSON
      const geojson = await fileToGeoJSON(file);

      onChange({
        mode: 'uploaded_file',
        geojson: geojson,
        fileName: file.name,
        fileSize: file.size,
        uploadPath: `pending-upload/${file.name}` // Will be updated during form submission
      });
    } catch (error) {
      console.error('Error processing file:', error);
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const isAssetSelected = (assetId) => {
    return value?.selectedAssetIds?.includes(assetId) || false;
  };

  const multipleAssets = field.validation?.multipleAssets !== false;
  const allowAssetReference = field.validation?.allowAssetReference !== false;
  const allowFileUpload = field.validation?.allowFileUpload !== false;

  return (
    <div className="space-y-3">
      {/* Tabs */}
      {allowAssetReference && allowFileUpload && (
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('asset')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'asset'
                ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Select Existing Asset{multipleAssets ? 's' : ''}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('file')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'file'
                ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Upload File
          </button>
        </div>
      )}

      {/* Asset Selection Tab */}
      {activeTab === 'asset' && allowAssetReference && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Select {multipleAssets ? 'one or more assets' : 'an asset'} with boundary data:
          </p>

          {loadingAssets ? (
            <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded border border-gray-200">
              Loading assets...
            </div>
          ) : totalAvailableAssets === 0 ? (
            <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded border border-gray-200">
              No assets with boundary data found. Create assets with GeoJSON boundaries first, or upload a file instead.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
              {assetGroups.map(group => (
                <div key={group.id} className="border-b last:border-b-0">
                  {/* Asset Group Header */}
                  <div className="bg-gray-100 px-3 py-2 border-b sticky top-0">
                    <div className="text-sm font-semibold text-gray-700">
                      {group.name}
                    </div>
                    {group.description && (
                      <div className="text-xs text-gray-500">
                        {group.description}
                      </div>
                    )}
                  </div>

                  {/* Assets in this group */}
                  {group.assets.map(asset => (
                    <label
                      key={asset.id}
                      className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                        isAssetSelected(asset.id) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <input
                        type={multipleAssets ? 'checkbox' : 'radio'}
                        checked={isAssetSelected(asset.id)}
                        onChange={() => handleAssetSelection(asset.id)}
                        className="h-4 w-4 text-eai-primary-accent focus:ring-eai-primary-accent border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {asset.name}
                        </div>
                        {asset.description && (
                          <div className="text-xs text-gray-500">
                            {asset.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400">
                          {asset.lat?.toFixed(4)}, {asset.lng?.toFixed(4)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}

          {value?.mode === 'asset_reference' && value.selectedAssetIds?.length > 0 && (
            <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
              ✓ {value.selectedAssetIds.length} asset{value.selectedAssetIds.length > 1 ? 's' : ''} selected
            </div>
          )}
        </div>
      )}

      {/* File Upload Tab */}
      {activeTab === 'file' && allowFileUpload && (
        <div className="space-y-2">
          <input
            type="file"
            onChange={handleFileUpload}
            accept={field.validation?.fileTypes?.join(',') || '.kml,.kmz,.geojson,.json'}
            disabled={uploading}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-eai-primary-accent file:text-white hover:file:bg-gray-400 disabled:opacity-50"
          />

          {uploading && (
            <div className="text-sm text-blue-600">
              Processing file...
            </div>
          )}

          {uploadError && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {uploadError}
            </div>
          )}

          {value?.mode === 'uploaded_file' && value.fileName && (
            <div className="bg-green-50 border border-green-200 rounded p-3 space-y-1">
              <div className="text-sm text-green-700 font-medium">
                ✓ File uploaded successfully
              </div>
              <div className="text-xs text-green-600">
                {value.fileName} ({(value.fileSize / 1024).toFixed(2)}KB)
              </div>
              {value.geojson?.features && (
                <div className="text-xs text-green-600">
                  {value.geojson.features.length} feature{value.geojson.features.length > 1 ? 's' : ''} detected
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-gray-500">
            Accepted formats: {field.validation?.fileTypes?.join(', ') || 'KML, KMZ, GeoJSON'}
            <br />
            Files will be automatically processed and simplified if needed.
          </div>
        </div>
      )}
    </div>
  );
};

// AssetLocationField Component - Point location: link an asset (coordinates resolved from the asset at run time — the value stores only the reference) or enter latitude/longitude manually. TODO: REFACTOR - Move asset data fetching to Redux (same as AssetBoundaryField above)
const AssetLocationField = ({ field, value, onChange }) => {
  const allowAssetReference = field.validation?.allowAssetReference !== false;
  const allowManualInput = field.validation?.allowManualInput !== false;

  const [activeTab, setActiveTab] = useState(() => {
    if (value?.mode === 'manual') return 'manual';
    if (value?.mode === 'asset_reference') return 'asset';
    return allowAssetReference ? 'asset' : 'manual';
  });
  const [assetGroups, setAssetGroups] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(true);
  // String state so partial input ("-", "4.") doesn't fight the number parse; String() keeps a stored 0 coordinate from rendering as empty
  const [latitude, setLatitude] = useState(
    value?.mode === 'manual' && value.latitude !== undefined ? String(value.latitude) : ''
  );
  const [longitude, setLongitude] = useState(
    value?.mode === 'manual' && value.longitude !== undefined ? String(value.longitude) : ''
  );

  // Fetch asset groups the first time the asset tab is shown — unlike boundaries, every asset has lat/lng, so no geojson filter (and the points-only query skips the geojson blobs entirely). The ref stops tab toggles from refetching, but only latches on success so a failed fetch can be retried (via the Retry button or the next tab switch).
  const hasFetchedAssets = useRef(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  useEffect(() => {
    const fetchAssetGroups = async () => {
      try {
        setLoadingAssets(true);
        setFetchFailed(false);
        // Every page of groups (AppSync returns one page per call)
        const { items: groupItems } = await listAllPages<any>(
          fetchAssetGroupsWithAssetPoints,
          "listAssetGroups",
          { limit: PICKER_GROUP_PAGE_SIZE },
          { label: "fetchAssetGroupsWithAssetPoints" }
        );

        const groups = groupItems
          .map(group => ({
            id: group.id,
            name: group.name,
            description: group.description,
            assets: group.assets.items
          }))
          .filter(group => group.assets.length > 0);

        setAssetGroups(groups);
        hasFetchedAssets.current = true;
      } catch (error) {
        console.error('Error fetching asset groups:', error);
        setFetchFailed(true);
      } finally {
        setLoadingAssets(false);
      }
    };

    if (activeTab === 'asset' && !hasFetchedAssets.current) {
      fetchAssetGroups();
    }
  }, [activeTab, retryToken]);

  const totalAvailableAssets = assetGroups.reduce((sum, group) => sum + group.assets.length, 0);

  // Inline feedback for a coordinate input; null when empty or valid. The empty-string guard matters because Number('') is 0
  const coordError = (str, min, max, label) => {
    if (str.trim() === '') return null;
    const n = Number(str);
    if (!Number.isFinite(n)) return `${label} must be a number`;
    if (n < min || n > max) return `${label} must be between ${min} and ${max}`;
    return null;
  };
  const latitudeError = coordError(latitude, -90, 90, 'Latitude');
  const longitudeError = coordError(longitude, -180, 180, 'Longitude');

  // Emit the manual value only when both coordinates are complete, in-range numbers; anything else emits null so step validation reports the field
  const emitManual = (latStr, lonStr) => {
    const bothComplete =
      latStr.trim() !== '' && lonStr.trim() !== '' &&
      !coordError(latStr, -90, 90, 'Latitude') &&
      !coordError(lonStr, -180, 180, 'Longitude');
    if (bothComplete) {
      onChange({ mode: 'manual', latitude: Number(latStr), longitude: Number(lonStr) });
    } else {
      onChange(null);
    }
  };

  const handleTabSwitch = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === 'manual') {
      // Restore the manual value if the inputs already hold complete coordinates
      emitManual(latitude, longitude);
    } else if (value?.mode !== 'asset_reference') {
      onChange(null);
    }
  };

  const selectedAssetId = value?.mode === 'asset_reference' ? value.selectedAssetIds?.[0] : undefined;
  const selectedAsset = selectedAssetId
    ? assetGroups.flatMap(g => g.assets).find(a => a.id === selectedAssetId)
    : undefined;

  return (
    <div className="space-y-3">
      {/* Tabs */}
      {allowAssetReference && allowManualInput && (
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => handleTabSwitch('asset')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'asset'
                ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Use Asset Location
          </button>
          <button
            type="button"
            onClick={() => handleTabSwitch('manual')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'manual'
                ? 'border-b-2 border-eai-primary-accent text-eai-primary-accent'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Manual Coordinates
          </button>
        </div>
      )}

      {/* Asset Selection Tab */}
      {activeTab === 'asset' && allowAssetReference && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Select an asset to use its location:
          </p>

          {loadingAssets ? (
            <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded border border-gray-200">
              Loading assets...
            </div>
          ) : fetchFailed ? (
            <div className="text-sm text-red-600 p-4 bg-red-50 rounded border border-red-200 flex items-center justify-between">
              <span>Couldn't load assets.</span>
              <button
                type="button"
                onClick={() => setRetryToken(t => t + 1)}
                className="ml-3 px-3 py-1 text-sm font-medium text-white bg-eai-primary-accent rounded hover:bg-gray-400"
              >
                Retry
              </button>
            </div>
          ) : totalAvailableAssets === 0 ? (
            <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded border border-gray-200">
              No assets found. Create an asset first{allowManualInput ? ', or enter coordinates manually instead' : ''}.
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
              {assetGroups.map(group => (
                <div key={group.id} className="border-b last:border-b-0">
                  {/* Asset Group Header */}
                  <div className="bg-gray-100 px-3 py-2 border-b sticky top-0">
                    <div className="text-sm font-semibold text-gray-700">
                      {group.name}
                    </div>
                    {group.description && (
                      <div className="text-xs text-gray-500">
                        {group.description}
                      </div>
                    )}
                  </div>

                  {/* Assets in this group */}
                  {group.assets.map(asset => (
                    <label
                      key={asset.id}
                      className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                        selectedAssetId === asset.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        checked={selectedAssetId === asset.id}
                        onChange={() => onChange({ mode: 'asset_reference', selectedAssetIds: [asset.id] })}
                        className="h-4 w-4 text-eai-primary-accent focus:ring-eai-primary-accent border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {asset.name}
                        </div>
                        {asset.description && (
                          <div className="text-xs text-gray-500">
                            {asset.description}
                          </div>
                        )}
                        <div className="text-xs text-gray-400">
                          {asset.lat?.toFixed(4)}, {asset.lng?.toFixed(4)}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}

          {selectedAssetId && (
            <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
              ✓ {selectedAsset ? selectedAsset.name : '1 asset'} selected
            </div>
          )}

          <div className="text-xs text-gray-500">
            The model will use the linked asset's current location each time it runs.
            Moving the asset updates the model automatically.
          </div>
        </div>
      )}

      {/* Manual Coordinates Tab */}
      {activeTab === 'manual' && allowManualInput && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Latitude
            </label>
            <input
              type="number"
              step="0.000001"
              min={-90}
              max={90}
              value={latitude}
              onChange={(e) => {
                setLatitude(e.target.value);
                emitManual(e.target.value, longitude);
              }}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              placeholder="e.g., 40.7128"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                latitudeError
                  ? 'border-red-300 focus:ring-red-400'
                  : 'border-gray-300 focus:ring-eai-primary-accent'
              }`}
            />
            {latitudeError && (
              <p className="mt-1 text-xs text-red-600">{latitudeError}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Longitude
            </label>
            <input
              type="number"
              step="0.000001"
              min={-180}
              max={180}
              value={longitude}
              onChange={(e) => {
                setLongitude(e.target.value);
                emitManual(latitude, e.target.value);
              }}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              placeholder="e.g., -74.0060"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                longitudeError
                  ? 'border-red-300 focus:ring-red-400'
                  : 'border-gray-300 focus:ring-eai-primary-accent'
              }`}
            />
            {longitudeError && (
              <p className="mt-1 text-xs text-red-600">{longitudeError}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// XYCurveField Component - Relationship curve built as x/y point pairs with a live graph preview (same editing pattern as the asset performance curve). Rows keep their entry order while editing; the emitted value is sorted by x.
const XYCurveField = ({ field, value, onChange, error }) => {
  const [rows, setRows] = useState(() =>
    curvePointsFromValue(value).map((p) => ({ x: String(p.x), y: String(p.y) }))
  );
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importErrors, setImportErrors] = useState([]);

  const xTitle = curveAxisTitle(field.validation?.xAxisLabel, field.validation?.xAxisUnit) || 'X';
  const yTitle = curveAxisTitle(field.validation?.yAxisLabel, field.validation?.yAxisUnit) || 'Y';

  // Rows where both cells hold a complete number — what the graph shows and what gets stored. The empty-string check matters: Number('') is 0, so without it a blank row would silently become a (0,0) point.
  const toPoints = (rowList) =>
    rowList
      .filter((row) => row.x !== '' && row.y !== '')
      .map((row) => ({ x: Number(row.x), y: Number(row.y) }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

  const rowIsComplete = (row) => toPoints([row]).length === 1;

  const emit = (nextRows) => {
    const points = sortCurvePoints(toPoints(nextRows));
    onChange(points.length > 0 ? { value: points } : null);
  };

  const handleCellChange = (index, key, rawValue) => {
    // Allow in-progress numbers: '', '-', '.', '-.', '3.', etc.
    if (!/^-?[0-9]*\.?[0-9]*$/.test(rawValue)) return;
    const nextRows = rows.map((row, i) => (i === index ? { ...row, [key]: rawValue } : row));
    setRows(nextRows);
    emit(nextRows);
  };

  // New rows start blank (and get focus) rather than duplicating the previous point; an incomplete row is simply not part of the stored curve yet.
  const handleAddPoint = () => {
    setRows([...rows, { x: '', y: '' }]);
  };

  // Enter on a completed last row starts the next point — fast manual entry
  const handleRowKeyDown = (e, index) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (index === rows.length - 1 && rowIsComplete(rows[index])) {
      handleAddPoint();
    }
  };

  const handleDeletePoint = (index) => {
    const nextRows = rows.filter((_, i) => i !== index);
    setRows(nextRows);
    emit(nextRows);
  };

  const handleImportApply = () => {
    const { points, errors: parseErrors } = parseCurveCsv(importText);
    if (parseErrors.length > 0) {
      setImportErrors(parseErrors);
      return;
    }
    if (points.length === 0) {
      setImportErrors(['No points found. Enter one point per line as: x,y']);
      return;
    }
    const nextRows = points.map((p) => ({ x: String(p.x), y: String(p.y) }));
    setRows(nextRows);
    emit(nextRows);
    setImportErrors([]);
    setImportText('');
    setShowImport(false);
  };

  const graphPoints = toPoints(rows);

  const cellClasses = `w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-eai-primary-accent ${
    error ? 'border-red-300' : 'border-gray-300'
  }`;
  // Half-filled rows get a soft amber border so it's obvious why they aren't on the graph / in the point count yet
  const partialCellClasses =
    'w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-eai-primary-accent border-amber-400 bg-amber-50';

  return (
    <div className="space-y-2">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Point table */}
        <div className="flex-1 border border-gray-200 rounded-md overflow-hidden">
          <div className="flex bg-gray-50 py-2 border-b border-gray-200">
            <div className="flex-1 text-center text-sm font-medium text-gray-700">{xTitle}</div>
            <div className="flex-1 text-center text-sm font-medium text-gray-700">{yTitle}</div>
            <div className="w-20 text-center text-sm font-medium text-gray-700">Actions</div>
          </div>
          <div className="max-h-[260px] overflow-y-auto">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-500 italic p-3">
                No points yet. Add points manually or import them.
              </p>
            ) : (
              rows.map((row, index) => {
                const isBlank = row.x === '' && row.y === '';
                const rowCellClasses = !rowIsComplete(row) && !isBlank ? partialCellClasses : cellClasses;
                return (
                <div key={index} className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 hover:bg-gray-50">
                  <div className="flex-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.x}
                      placeholder="x"
                      // Focus the row "+ Add Point" just created (mount-only)
                      autoFocus={index === rows.length - 1 && isBlank}
                      onChange={(e) => handleCellChange(index, 'x', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={rowCellClasses}
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.y}
                      placeholder="y"
                      onChange={(e) => handleCellChange(index, 'y', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={rowCellClasses}
                    />
                  </div>
                  <div className="w-20 flex justify-center">
                    <button
                      type="button"
                      onClick={() => handleDeletePoint(index)}
                      className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                );
              })
            )}
          </div>
          <div className="flex gap-2 p-2 bg-gray-50 border-t border-gray-200">
            <button
              type="button"
              onClick={handleAddPoint}
              className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-eai-primary-accent rounded-md hover:bg-gray-400"
            >
              + Add Point
            </button>
            <button
              type="button"
              onClick={() => {
                setShowImport(!showImport);
                setImportErrors([]);
              }}
              className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
            >
              {showImport ? 'Close Import' : 'Import'}
            </button>
          </div>
        </div>

        {/* Live graph preview — fixed height so adding rows doesn't stretch it */}
        <div className="flex-1 h-[260px] self-start border border-gray-200 rounded-md p-2">
          {graphPoints.length > 0 ? (
            <PCurveGraph data={graphPoints} xAxisLabel={xTitle} yAxisLabel={yTitle} />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">
              Curve preview appears here
            </div>
          )}
        </div>
      </div>

      {/* CSV import panel */}
      {showImport && (
        <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50">
          <label className="block text-sm font-medium text-gray-700">
            Import points (replaces the current curve)
          </label>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={'One point per line, comma separated:\n1, 2\n2, 3\n6, 4'}
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-eai-primary-accent"
          />
          {importErrors.length > 0 && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded space-y-0.5">
              {importErrors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowImport(false);
                setImportErrors([]);
              }}
              className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImportApply}
              className="px-3 py-1.5 text-sm font-medium text-white bg-eai-primary-accent rounded-md hover:bg-gray-400"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">
        {graphPoints.length} point{graphPoints.length === 1 ? '' : 's'} — a curve needs at least 2. Points are sorted by {xTitle} when saved.
      </p>
    </div>
  );
};

export default SchemaFieldRenderer;